import type { ChangeEventHandler } from 'react';
import {
  Alert,
  Box,
  Card,
  Checkbox,
  Chip,
  Text,
  Title,
} from '@nimbus-ds/components';
import { FreeShippingDeliveryMethodsDto } from '@tiendanube/common';
import { SelectOrEditLink, Stack } from 'commons/components';
import { useModal } from 'commons/hooks';
import {
  DeliveryMethodsModalAside,
  StatusTagItem,
} from 'domains/Marketing/FreeShipping/components';
import { useTranslationMarketing } from 'domains/Marketing/hook';
import { useFreeShippingItemStatus } from '../../../hooks/useFreeShippingItemStatus';

interface DeliveryMethodsCardProps {
  methods: FreeShippingDeliveryMethodsDto[];
  showRequiredError: boolean;
  onChange: (methods: FreeShippingDeliveryMethodsDto[]) => void;
  onlyCheapestShipping?: boolean;
  onChangeOnlyCheapestShipping?: ChangeEventHandler<HTMLInputElement>;
  freeShippingId?: string;
}

export function DeliveryMethodsCard({
  methods,
  showRequiredError,
  onChange,
  onlyCheapestShipping = false,
  onChangeOnlyCheapestShipping,
  freeShippingId,
}: Readonly<DeliveryMethodsCardProps>) {
  const t = useTranslationMarketing(
    'freeShipping.settingsPage.deliveryMethods',
  );
  const tConditions = useTranslationMarketing(
    'freeShipping.settingsPage.conditions',
  );
  const { status } = useFreeShippingItemStatus({ id: freeShippingId });
  const [isShowModal, openModal, closeModal] = useModal();
  const isEmpty = !methods.length;

  return (
    <>
      <Card>
        <Card.Header>
          <Box
            alignItems="center"
            display="flex"
            justifyContent="space-between"
          >
            <Title as="h3">{t('title')}</Title>
            {status !== undefined && <StatusTagItem status={status} />}
          </Box>
        </Card.Header>
        <Card.Body>
          <Stack column align="flex-start" spacing="loose">
            {isEmpty && <Text> {t('body')} </Text>}
            {showRequiredError && (
              <Alert appearance="danger">{t('required')}</Alert>
            )}
            {!isEmpty && (
              <Box display="flex" gap="2" flexWrap="wrap">
                {methods.map(({ idMethod, idOption, description }) => (
                  <Chip
                    key={`${idMethod}-${idOption}`}
                    text={description}
                    removable
                    onClick={() =>
                      onChange(
                        methods.filter(
                          (current) =>
                            !(
                              current.idMethod === idMethod &&
                              current.idOption === idOption
                            ),
                        ),
                      )
                    }
                  />
                ))}
              </Box>
            )}
          </Stack>
        </Card.Body>
        <Card.Footer>
          <Box display="flex" flexDirection="column" gap="4">
            <SelectOrEditLink isEdit={!isEmpty} onSelect={openModal} />
            {onChangeOnlyCheapestShipping && (
              <Checkbox
                name="onlyCheapestShipping"
                label={tConditions('onlyCheapestShipping')}
                onChange={onChangeOnlyCheapestShipping}
                checked={onlyCheapestShipping}
              />
            )}
          </Box>
        </Card.Footer>
      </Card>
      <DeliveryMethodsModalAside
        isOpen={isShowModal}
        selected={methods}
        onClose={closeModal}
        onChange={onChange}
      />
    </>
  );
}
